/**
 * createPrefsStore — the two-tier store behind every `NavPrefs`-shaped
 * preference (sidebar order, account tab order).
 *
 * This was [[useNavPrefs]]'s body. A second surface wanted the identical
 * mechanics against a different `preference_type`, and copying 100 lines of
 * cache/fetch/broadcast logic is how two stores drift: one grows a fix for a
 * failed save or a private-mode throw and the other does not. So the mechanics
 * live here once and each surface supplies only its two names.
 *
 * Both tiers are load-bearing and neither is redundant:
 *   - localStorage is the FIRST PAINT source. The strip renders before any
 *     network call resolves; without a local mirror every reload flashes the
 *     shipped order and then reshuffles under the cursor.
 *   - `user_preferences.<type>` is the SOURCE OF TRUTH — it follows the user
 *     across devices, which is the point of arranging on a laptop and opening
 *     on a phone.
 *
 * Writes are optimistic: the UI moves on the gesture, the PUT follows. A failed
 * save KEEPS the local value and surfaces an error rather than snapping back —
 * reverting a deliberate rearrangement because the network blinked is worse
 * than a layout that is ahead of the server.
 */

import { useCallback, useEffect, useState } from 'react';
import userPreferencesService from '../services/userPreferences';

/**
 * @param {object} opts
 * @param {string} opts.type      `user_preferences.preference_type`
 * @param {string} opts.localKey  localStorage key for the first-paint mirror
 * @param {(raw: unknown) => object} opts.normalize  never-throws coercion
 */
export function createPrefsStore({ type, localKey, normalize }) {
  function readLocal() {
    try {
      const raw = localStorage.getItem(localKey);
      return normalize(raw);
    } catch {
      // Private mode, blocked storage, corrupt blob — all degrade to "no
      // customization", which is always a usable layout.
      return normalize(null);
    }
  }

  function writeLocal(prefs) {
    try { localStorage.setItem(localKey, JSON.stringify(prefs)); } catch { /* quota / private mode — the server copy still stands */ }
  }

  // One in-memory copy per tab so every mount agrees INSTANTLY. The strip and
  // the reorder panel are separate mounts, and the strip must move as you drag.
  let current = null;
  const subscribers = new Set();

  function getCurrent() {
    if (!current) current = readLocal();
    return current;
  }

  function broadcast(prefs) {
    current = prefs;
    writeLocal(prefs);
    for (const fn of subscribers) fn(prefs);
  }

  /** Is there a session to sync against? Signed out, local-only is all there is. */
  function hasAccount() {
    try { return !!userPreferencesService.getAuthToken(); } catch { return false; }
  }

  function usePrefs() {
    const [prefs, setPrefs] = useState(getCurrent);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
      subscribers.add(setPrefs);
      return () => { subscribers.delete(setPrefs); };
    }, []);

    // Deliberately NOT wired to AuthContext: these render in shells that mount
    // no AuthProvider, and a layout preference is not worth making the sidebar
    // un-mountable over. The token check is enough to know whether a
    // round-trip can succeed.
    useEffect(() => {
      if (!hasAccount()) { setLoaded(true); return undefined; }
      let cancelled = false;

      userPreferencesService
        .fetchPreference(type)
        .then((value) => {
          if (cancelled) return;
          broadcast(normalize(value));
          setLoaded(true);
        })
        .catch(() => {
          // Offline or preferences down — the local mirror stands. Staying
          // customized beats reverting to defaults mid-flight.
          if (!cancelled) setLoaded(true);
        });

      return () => { cancelled = true; };
    }, []);

    const save = useCallback(async (next) => {
      const normalized = normalize(next);
      broadcast(normalized);              // optimistic — the UI moves now
      setError(null);

      if (!hasAccount()) return normalized;

      setSaving(true);
      try {
        await userPreferencesService.updatePreference(type, normalized);
      } catch (e) {
        setError(e?.message || 'Could not save to your account');
      } finally {
        setSaving(false);
      }
      return normalized;
    }, []);

    return { prefs, setPrefs: save, loaded, saving, error };
  }

  return { usePrefs, hasAccount, getCurrent, __broadcast: broadcast };
}

export default createPrefsStore;
